import { i18n, type Messages } from '@lingui/core';

// [i18n wave 1] Dashboard-side locale runtime — spec 4234329d.
//
// This is the INTERFACE language only. What Mag advertises in is the
// market language on brand_markets.language and never passes through here.

/** Mirrors SUPPORTED_LOCALES in apps/api/src/i18n/locale.ts. The API is
 *  the authority — it resolves and returns the locale on /api/me — but the
 *  client needs the list to make a pre-flight guess before that call
 *  answers, and to reject a value it has no catalog for. */
export const DASHBOARD_LOCALES = ['en', 'es'] as const;
export type DashboardLocale = (typeof DASHBOARD_LOCALES)[number];

export const DEFAULT_LOCALE: DashboardLocale = 'en';

/** Not a language anyone reads. Lingui renders it as accented,
 *  length-padded text, so a string that was never extracted shows up as
 *  plain English against padded surroundings — visible before a single
 *  word of Spanish exists. It also previews the ~15-20% length growth
 *  Spanish brings, which is where fixed-width chrome breaks.
 *
 *  Dev-only by construction: activation is gated on import.meta.env.DEV,
 *  so a production bundle can never enter it even via a crafted URL. The
 *  compiled catalog is still emitted as a lazy chunk in a prod build —
 *  it inflates the deploy artifact, but production never requests it. */
export const PSEUDO_LOCALE = 'pseudo';

/** Locales activateLocale will actually load. Superset of the locales a
 *  founder can CHOOSE, which is DASHBOARD_LOCALES. */
export type ActivatableLocale = DashboardLocale | typeof PSEUDO_LOCALE;

/** True when this dev session asked for pseudo-localization via ?pseudo.
 *
 *  A URL flag rather than an env var so QA can toggle it on any running
 *  dev server without a restart, and so it is obvious from the address bar
 *  which mode a screenshot was taken in. */
export function pseudoLocaleRequested(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('pseudo');
  } catch {
    return false;
  }
}

export function isDashboardLocale(value: unknown): value is DashboardLocale {
  return typeof value === 'string' && DASHBOARD_LOCALES.includes(value as DashboardLocale);
}

/** A guess good enough to render with before /api/me answers.
 *
 *  Deliberately NOT the real resolution — that lives server-side and
 *  accounts for users.locale, which the browser can't see. This only
 *  exists so the first paint isn't blank or wrongly English for a Spanish
 *  browser; the server's answer overrides it as soon as it lands. */
export function bootstrapLocale(): DashboardLocale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  // navigator.languages, not navigator.language: a browser whose top
  // preference is a language we don't serve (fr-FR) may well list one we
  // do further down (es). Reading only the first entry throws that away
  // and falls to English for someone who told us they read Spanish.
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const primary = tag?.split('-')[0]?.toLowerCase();
    if (isDashboardLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

export async function activateLocale(
  locale: ActivatableLocale,
  options: { shouldApply?: () => boolean } = {},
): Promise<void> {
  const mod = (await import(`../locales/${locale}/messages.po`)) as { messages: Messages };
  // The caller decides whether this result is still wanted. Two
  // activations are routinely in flight at once — the browser guess on
  // mount and the authoritative locale once /api/me answers — and both
  // await a dynamic import, so without this the guess can resolve second
  // and overwrite the founder's real choice. A Spanish-browser founder who
  // explicitly set English would land on a Spanish UI, which is the exact
  // failure the user-locale/market-language split exists to prevent.
  //
  // Expressed as a predicate rather than a module-level counter so the
  // liveness question belongs to the caller's lifecycle (a React effect's
  // cleanup) instead of to shared mutable state in this module.
  if (options.shouldApply && !options.shouldApply()) return;
  i18n.load(locale, mod.messages);
  i18n.activate(locale);
  if (typeof document !== 'undefined') {
    // Pseudo is not a real language tag; claiming it would make screen
    // readers and translation prompts behave unpredictably. Declare the
    // source language the pseudo text was derived from instead.
    document.documentElement.lang = locale === PSEUDO_LOCALE ? DEFAULT_LOCALE : locale;
  }
}

/** Put i18n into a usable state synchronously, before any catalog has
 *  loaded. Lingui throws if a component renders while no locale is
 *  active, so this runs at module load and the real catalog swaps in
 *  underneath. Empty messages are fine — an absent entry falls back to the
 *  source text. */
export function bootstrapI18nSync(locale: DashboardLocale = DEFAULT_LOCALE): void {
  i18n.load(locale, {});
  i18n.activate(locale);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

export { i18n };
